import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { ConversationProvider, useConversation } from '@elevenlabs/react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Mic, MicOff, PhoneOff, Loader2, Volume2 } from 'lucide-react';

const Session = ({ api, authHeaders, onFinished }) => {
  const [lines, setLines] = useState([]);
  const [starting, setStarting] = useState(false);
  const logRef = useRef(null);

  const conversation = useConversation({
    onMessage: (m) => {
      if (!m?.message) return;
      setLines(prev => [...prev, { role: m.source === 'user' ? 'user' : 'assistant', content: m.message }]);
    },
    onError: (e) => toast.error(typeof e === 'string' ? e : 'Connessione alla voce interrotta'),
    onDisconnect: () => onFinished?.(),
  });

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [lines]);

  const connected = conversation.status === 'connected';

  const start = async () => {
    setStarting(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const res = await axios.get(`${api}/phone/agent/voice-token`, authHeaders());
      setLines([]);
      await conversation.startSession({ conversationToken: res.data.token, connectionType: 'webrtc' });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Microfono non disponibile');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="voice-test-panel">
      <div className="flex flex-wrap items-center gap-2">
        {!connected ? (
          <Button onClick={start} disabled={starting || conversation.status === 'connecting'}
            className="min-h-[44px]" data-testid="voice-start-btn">
            {starting || conversation.status === 'connecting'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Mic className="w-4 h-4 mr-2" />}
            Parla con l'assistente
          </Button>
        ) : (
          <>
            <Button variant="destructive" onClick={() => conversation.endSession()}
              className="min-h-[44px]" data-testid="voice-stop-btn">
              <PhoneOff className="w-4 h-4 mr-2" /> Chiudi
            </Button>
            <Button variant="outline" size="icon" className="min-h-[44px] min-w-[44px]"
              onClick={() => conversation.setMuted(!conversation.isMuted)} data-testid="voice-mute-btn">
              {conversation.isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
            <Badge variant="secondary" className="gap-1" data-testid="voice-state-badge">
              {conversation.isSpeaking ? <><Volume2 className="w-3 h-3" /> sta parlando</> : 'ti ascolta'}
            </Badge>
          </>
        )}
      </div>

      {(connected || lines.length > 0) && (
        <div ref={logRef} className="border rounded-lg p-3 space-y-2 max-h-[300px] overflow-auto bg-muted/30"
          data-testid="voice-transcript">
          {lines.length === 0 && <p className="text-sm text-muted-foreground">Parla pure: la trascrizione compare qui.</p>}
          {lines.map((l, i) => (
            <div key={i} className={`flex ${l.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${l.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-background border'}`}>
                {l.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const VoiceTest = (props) => (
  <ConversationProvider>
    <Session {...props} />
  </ConversationProvider>
);
